
import ActionsNotes from '@/components/_ActionsNotes/index.vue';
import InvoiceDisputesTable from '@/components/_InvoiceDisputes/InvoiceDisputesTable.vue';
import { Risolto_Document_Attachment_Type_Enum, Risolto_Task_Bool_Exp, ViewsInvoiceDisputesRecipient_RecipientAttachmentsDocument, ViewsInvoiceDisputesRecipient_RecipientRecord, ViewsInvoiceDisputesRecipient_RecipientRecordQueryVariables, ViewsInvoiceDisputes_TasksDocument, ViewsInvoiceDisputes_TasksQueryVariables, ViewsInvoiceDisputes_UserInterfaceDetailsDocument } from '@/generated/graphql';
import InvoiceDisputeHelper from '@/helpers/invoice-dispute';
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import { IDisputesState } from '@/store/modules/disputes';
import Filters from './components/Filters.vue';
import gql from 'graphql-tag';
import Contacts from '@/components/_Contacts/index.vue';
import MainContacts from '@/components/_MainContacts/index.vue';
import JsonEditor from '@/components/JsonEditor/index.vue';
import CustomFieldsList from '@/components/_CustomFields/CustomFieldsList.vue';
import AttachmentsDialog from '@/components/_dialogs/AttachmentsDialog.vue';
import { S3_Attachment } from '@/schema/attachment';
import { recipientDeleteAttachment, recipientUploadAttachment } from '@/api/recipient';
import { hasLicenseAndPermission } from '@/helpers/permission-helper';

gql`
query ViewsInvoiceDisputesRecipient_recipientRecord ($recipient: uuid!) {
  recipientRecord: risolto_recipient_by_pk(reference: $recipient) {
    reference
    name
	legal_entity
	custom_fields
  }
}
`;

gql`
query ViewsInvoiceDisputesRecipient_recipientAttachments ($recipient: uuid!) {
	recipientAttachments: risolto_recipient_attachment(
		where: {
			recipient: { _eq: $recipient }
		}
	) {
		metadata
	}
}
`;

export default Vue.extend({
	components: {
		ActionsNotes,
		AttachmentsDialog,
		Contacts,
		CustomFieldsList,
		JsonEditor,
		MainContacts,
		Filters,
		InvoiceDisputesTable
	},
	props: {
		recipient: { type: String, required: true },
		legalEntity: { type: String, required: true }
	},
	apollo: {
		recipientRecord: {
			query: ViewsInvoiceDisputesRecipient_RecipientRecord,
			variables(): ViewsInvoiceDisputesRecipient_RecipientRecordQueryVariables {
				return {
					recipient: this.recipient
				};
			}
		},
		tasks: {
			query: ViewsInvoiceDisputes_TasksDocument,
			variables(): ViewsInvoiceDisputes_TasksQueryVariables {
				return {
					where: this.where
				};
			}
		},
		recipientAttachments: {
			query: ViewsInvoiceDisputesRecipient_RecipientAttachmentsDocument,
			variables() {
				return {
					recipient: this.recipient
				};
			},
			update: data => data.recipientAttachments.map((attachment) => {
				return {
					...attachment.metadata
				};
			})
		},
		userInterfaceDetails: {
			query: ViewsInvoiceDisputes_UserInterfaceDetailsDocument,
			variables() {
				return {
					participant: this.selectedParticipantStore
				};
			},
			update: data => data.userInterfaceDetails[0],
			skip() {
				return !this.recipient;
			}
		}
	},
	data() {
		return {
			attachmentsDialogVisible: false,
			recipientAttachments: [],
			tasks: [],
			showCustomFields: false,
			allowEditContacts: false
		};
	},
	computed: {
		...mapState('disputes', {
			filterDisputeCodes: (state: IDisputesState) => state.filterDisputeCodes,
			filterAssignee: (state: IDisputesState) => state.filterAssignee,
			filterDue: (state: IDisputesState) => state.filterDue
		}),
		...mapGetters(['env', 'selectedParticipantStore']),
		invoices() {
			return this.tasks.map((task) => {
				return task.invoice_dispute_by_reference.invoice.reference;
			});
		},
		where(): Risolto_Task_Bool_Exp {
			let where: Risolto_Task_Bool_Exp = {};
			where['participant'] = { _eq: this.$store.state.risolto.selectedParticipantStore };
			where['legal_entity'] = { _eq: this.legalEntity };
			where['type'] = { _eq: 'DISPUTE' };
			where['status'] = { _eq: 'OPEN' };
			where['invoice_dispute_by_reference'] = {
				recipient_by_reference: {
					reference: { _eq: this.recipient }
				}
			};

			if (this.filterDisputeCodes && this.filterDisputeCodes.length > 0) {
				where['invoice_dispute_by_reference'] = {
					dispute_code_by_reference: {
						code: { _in: this.filterDisputeCodes }
					}
				};
			}

			if (this.filterAssignee) {
				if (this.filterAssignee === 'unassigned') {
					where['assignee'] = { _is_null: true };
				} else if (this.filterAssignee !== 'all') {
					where['assignee'] = { _eq: this.filterAssignee };
				}
			}

			if (this.filterDue) {
				where['due_date'] = { _lte: this.filterDue };
			}

			return where;
		}
	},
	watch: {
		invoices() {
			const selectedInvoices = [] as { reference: string, invoice_number: string }[];
			const selectedReferences = new Set();

			this.tasks.forEach((task) => {
				const { reference, invoice_number } = task.invoice_dispute_by_reference.invoice;

				if (!selectedReferences.has(reference)) {
					selectedReferences.add(reference);
					selectedInvoices.push({ reference, invoice_number });
				}
			});

			this.$store.dispatch('risolto/setSelectedInvoices', selectedInvoices);
		}
	},
	created() {
		const assignee = this.$route.query.assignee;
		if (assignee) {
			this.$store.commit('disputes/SET_FILTER_ASSIGNEE', assignee);
		}
	},
	methods: {
		handleAttachments() {
			this.attachmentsDialogVisible = true;
		},
		async handleAttachmentUpload(attachment: S3_Attachment) {
			try {
				await recipientUploadAttachment(
					this.selectedParticipantStore,
					this.recipientRecord.legal_entity,
					this.recipientRecord.reference,
					Risolto_Document_Attachment_Type_Enum.Pdf,
					attachment.name,
					attachment
				);

				this.recipientAttachments = [...this.recipientAttachments, attachment];
			} catch (e) {
				this.$notify({
					title: this.$t('invoice-disputes.error') as string,
					message: this.$t('invoice-disputes.attachment-can-not-be-uploaded') as string,
					type: 'error'
				});
				return;
			}
		},
		async handleAttachmentRemove(attachment: S3_Attachment) {
			try {
				await recipientDeleteAttachment(attachment.key);
				this.recipientAttachments = this.recipientAttachments.filter((item) => {
					return item.key !== attachment.key;
				});
			} catch (e) {
				this.$notify({
					title: this.$t('invoice-disputes.error') as string,
					message: this.$t('invoice-disputes.attachment-can-not-be-removed') as string,
					type: 'error'
				});
				return;
			}
		},
		isDue(row) {
			return InvoiceDisputeHelper.isDue(row.until);
		},
		isOverdue(row) {
			return InvoiceDisputeHelper.isOverDue(row.until);
		}
	}
});
